<template>
    <div class="attachment-gallery-modal">
        <a
            v-for="(attachment, i) in attachments"
            :key="`file_${i}`"
            href="#"
            @click.prevent="downloadAttachment(attachment)"
            class="attachment-modal-row no-underline"
            :class="{'opacity-50': downloadingFiles[attachment.id]}"
        >
            <div class="attachment-modal-icon-wrap">
                <img
                    :src="getFileIcon(attachment.icon)"
                    :alt="attachment.name"
                    class="attachment-modal-icon"
                />
                <LoadingSpinner
                    v-if="downloadingFiles[attachment.id]"
                    class="attachment-modal-spinner"
                />
            </div>
            <div class="attachment-modal-copy">
                <!-- Название файла -->
                <div
                    class="attachment-modal-name"
                    :title="attachment.name"
                >
                    {{ attachment.name }}
                </div>
                <!-- Размер файла -->
                <div class="attachment-modal-meta">
                    {{ formatFileSize(attachment.size) }}
                    ({{ getFileDirection(attachment) }})
                </div>

            </div>
        </a>
    </div>
</template>

<script>
import urls from "@/api/urls.js";
import {useAccountStore} from "@/stores/AccountStore.js";
import VueEasyLightbox from 'vue-easy-lightbox';
import {loadImageWithCache} from "@/helpers/imageLoader.js";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";


export default {
    name: "AttachmentsGallery",
    components: {
        LoadingSpinner,
        VueEasyLightbox
    },
    computed: {
        urls() {
            return urls
        },
        token() {
            return useAccountStore().token
        },
    },
    props: {
        attachments: {
            type: Array,
            default: () => [],
        },
        maxWidth: {
            type: String,
            default: '100px',
        }
    },
    data() {
        return {
            downloadingFiles: {}
        }
    },
    methods: {
        async downloadAttachment(attachment) {
            // Показываем индикатор загрузки
            this.downloadingFiles[attachment.id] = true;

            try {
                const url = urls.chat_attachment_url(attachment.id);
                const blobUrl = await loadImageWithCache(url, this.token);

                if (blobUrl) {
                    // Создаем временную ссылку для скачивания
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = attachment.name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error('Error downloading attachment:', error);
            } finally {
                this.downloadingFiles[attachment.id] = false;
            }
        },
        getFileIcon(type) {
            if (type === 'word') {
                return '/icons/word-96.png';
            }
            if (type === 'excel') {
                return '/icons/xls-96.png';
            }
            if (type === 'powerpoint') {
                return '/icons/powerpoint-96.png';
            }
            if (type === 'pdf') {
                return '/icons/pdf_2-96.png';
            }
            if (type === 'zip') {
                return '/icons/archive_folder-96.png';
            }
            return '/icons/file-96.png';
        },
        formatFileSize(sizeInBytes) {
            if (sizeInBytes < 1024) {
                return sizeInBytes + ' B';
            } else if (sizeInBytes < 1048576) {
                return (sizeInBytes / 1024).toFixed(2) + ' KB';
            } else {
                return (sizeInBytes / 1048576).toFixed(2) + ' MB';
            }
        },
        getFileDirection(file) {
            if(file.isPatient){
                if(file.isMine) return this.$t('Я - врачу');
                return this.$t('Врач - мне');
            }else{
                if(file.isMine) return this.$t('Я - пациенту');
                return this.$t('Пациент - мне');
            }
            return this.$t('файл от пациента');
        }
    }
};
</script>

<style scoped>
.attachment-gallery-modal {
    display: grid;
    gap: 0.42rem;
}

.attachment-modal-row {
    display: grid;
    grid-template-columns: 2.45rem minmax(0, 1fr);
    align-items: center;
    gap: 0.65rem;
    max-width: min(100%, 22rem);
    padding: 0.4rem 0.55rem;
    border: 1px solid var(--panel-border-color, #eef5f6);
    border-radius: var(--radius-md, 6px);
    background-color: rgba(255, 255, 255, 0.64);
    color: inherit;
    text-decoration: none;
}

.attachment-modal-row:hover,
.attachment-modal-row:focus {
    border-color: var(--main-color-20, rgba(36, 168, 180, 0.2));
    background-color: rgba(36, 168, 180, 0.045);
    color: inherit;
    text-decoration: none;
}

:global([data-bs-theme="dark"]) .attachment-modal-row {
    border-color: var(--panel-border-color, #3d5055);
    background-color: var(--surface-overlay-bg, #304044);
    color: var(--text-color, #e5ecee);
}

:global([data-bs-theme="dark"]) .attachment-modal-row:hover,
:global([data-bs-theme="dark"]) .attachment-modal-row:focus {
    border-color: var(--main-color-30, rgba(36, 168, 180, 0.3));
    background-color: var(--main-color-10, rgba(36, 168, 180, 0.1));
    color: var(--text-color, #e5ecee);
}

.attachment-modal-icon-wrap {
    position: relative;
    width: 2.45rem;
    height: 2.45rem;
}

.attachment-modal-icon {
    width: 2.45rem;
    height: 2.45rem;
    object-fit: contain;
    border: 1px solid var(--panel-border-color, #eef5f6);
    border-radius: var(--radius-sm, 4px);
    background-color: var(--surface-bg, #fff);
    padding: 0.16rem;
}

.attachment-modal-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1.25rem;
    height: 1.25rem;
    transform: translate(-50%, -50%);
}

.attachment-modal-copy {
    min-width: 0;
}

.attachment-modal-name {
    overflow: hidden;
    max-width: v-bind(maxWidth);
    color: var(--links-color, var(--main-color, #24a8b4));
    font-size: 0.9rem;
    font-weight: 500;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-modal-meta {
    overflow: hidden;
    margin-top: 0.12rem;
    color: var(--secondary-color, #666);
    font-size: 0.76rem;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

</style>
